import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card } from "../panel/PanelShell";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Field, inputCls } from "../forms/Fields";
import { api, formatApiError } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { useSettings } from "../../context/SettingsContext";

const BLANK_POST = { title: "", slug: "", category: "Market Insights", author: "Quantora Research Desk", excerpt: "", content: "", cover_image: "", published: true };

function PostDialog({ post, onClose, onSaved }) {
  const [f, setF] = useState(BLANK_POST);
  useEffect(() => { if (post) setF({ ...BLANK_POST, ...post }); }, [post]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const body = { title: f.title, slug: f.slug, category: f.category, author: f.author, excerpt: f.excerpt, content: f.content, cover_image: f.cover_image, published: f.published };
    try {
      if (post.id) await api.put(`/admin/blog/${post.id}`, body);
      else await api.post("/admin/blog", body);
      toast.success("Post saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Dialog open={!!post} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader><DialogTitle>{post?.id ? "Edit post" : "New blog post"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2" data-testid="post-form">
          <div className="sm:col-span-2"><Field label="Title" id="b-title"><input id="b-title" required minLength={3} className={inputCls} value={f.title} onChange={set("title")} data-testid="post-title-input" /></Field></div>
          <Field label="Slug (optional)" id="b-slug"><input id="b-slug" className={inputCls} value={f.slug} onChange={set("slug")} data-testid="post-slug-input" /></Field>
          <Field label="Category" id="b-cat"><input id="b-cat" required className={inputCls} value={f.category} onChange={set("category")} data-testid="post-category-input" /></Field>
          <Field label="Author" id="b-author"><input id="b-author" className={inputCls} value={f.author} onChange={set("author")} data-testid="post-author-input" /></Field>
          <Field label="Cover image URL" id="b-img"><input id="b-img" className={inputCls} value={f.cover_image} onChange={set("cover_image")} data-testid="post-image-input" /></Field>
          <div className="sm:col-span-2"><Field label="Excerpt" id="b-ex"><input id="b-ex" required minLength={5} className={inputCls} value={f.excerpt} onChange={set("excerpt")} data-testid="post-excerpt-input" /></Field></div>
          <div className="sm:col-span-2"><Field label="Content (use ## for headings, blank line between paragraphs)" id="b-content"><Textarea id="b-content" required minLength={10} rows={10} value={f.content} onChange={set("content")} data-testid="post-content-input" /></Field></div>
          <label className="flex items-center gap-3 text-sm text-slate-700"><Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="post-published-switch" /> Published</label>
          <button data-testid="post-save-btn" className="rounded-full bg-navy py-3 text-sm font-semibold text-white sm:col-span-2">Save post</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if (window.confirm("Delete this post?")) { await api.delete(`/admin/blog/${id}`); toast.success("Post deleted"); load(); } };
  return (
    <div className="space-y-5">
      <div className="flex justify-end"><button onClick={() => setEditing({ ...BLANK_POST })} data-testid="post-create-btn" className="inline-flex items-center gap-2 rounded-full bg-mint px-5 py-2.5 text-sm font-semibold text-navy"><Plus size={16} /> New post</button></div>
      <div className="space-y-3">
        {posts.map((p) => (
          <Card key={p.id} className="flex items-center gap-4" data-testid={`admin-post-${p.slug}`}>
            {p.cover_image && <img src={p.cover_image} alt="" className="hidden h-16 w-24 rounded-lg object-cover sm:block" />}
            <div className="flex-1"><p className="font-semibold text-navy">{p.title}</p><p className="text-xs text-slate-500">{p.category} · {fmtDate(p.created_at)} · {p.published ? <span className="text-mint-600">Published</span> : "Draft"}</p></div>
            <button onClick={() => setEditing(p)} data-testid={`post-edit-${p.slug}`} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-navy" aria-label="Edit"><Pencil size={15} /></button>
            <button onClick={() => remove(p.id)} data-testid={`post-delete-${p.slug}`} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label="Delete"><Trash2 size={15} /></button>
          </Card>
        ))}
      </div>
      <PostDialog post={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}

function ServiceDialog({ svc, onClose, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => { if (svc) setF({ ...svc, itemsText: svc.items.map((i) => `${i.title} | ${i.description}`).join("\n") }); }, [svc]);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const items = f.itemsText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const [t, ...d] = l.split("|"); return { title: t.trim(), description: d.join("|").trim() }; });
    try {
      await api.put(`/admin/services/${svc.slug}`, { title: f.title, tagline: f.tagline, summary: f.summary, activity_type: f.activity_type, compliance_note: f.compliance_note, items, enabled: f.enabled });
      toast.success("Service updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Dialog open={!!svc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader><DialogTitle>Edit service</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4" data-testid="service-form">
          <Field label="Title" id="sv-title"><input id="sv-title" required className={inputCls} value={f.title} onChange={set("title")} data-testid="service-title-input" /></Field>
          <Field label="Tagline" id="sv-tag"><input id="sv-tag" required className={inputCls} value={f.tagline} onChange={set("tagline")} data-testid="service-tagline-input" /></Field>
          <Field label="Activity type (education / guidance / regulated)" id="sv-act"><input id="sv-act" required className={inputCls} value={f.activity_type} onChange={set("activity_type")} data-testid="service-activity-input" /></Field>
          <Field label="Summary" id="sv-sum"><Textarea id="sv-sum" required rows={3} value={f.summary} onChange={set("summary")} data-testid="service-summary-input" /></Field>
          <Field label="Compliance note" id="sv-comp"><Textarea id="sv-comp" required rows={4} value={f.compliance_note} onChange={set("compliance_note")} data-testid="service-compliance-input" /></Field>
          <Field label="Items (one per line: Title | Description)" id="sv-items"><Textarea id="sv-items" required rows={7} value={f.itemsText} onChange={set("itemsText")} data-testid="service-items-input" /></Field>
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="service-enabled-switch" /> Visible on website</label>
          <button data-testid="service-save-btn" className="rounded-full bg-navy py-3 text-sm font-semibold text-white">Save service</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ServicesAdmin() {
  const { reload } = useSettings();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/services").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {rows.map((s) => (
        <Card key={s.slug} data-testid={`admin-service-${s.slug}`}>
          <div className="flex justify-between gap-3"><div><p className="text-xs font-semibold uppercase tracking-wider text-gold-600">{s.activity_type}</p><h3 className="mt-1 font-bold text-navy">{s.title}</h3></div>
            <button onClick={() => setEditing(s)} data-testid={`service-edit-${s.slug}`} className="h-fit rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-navy" aria-label="Edit"><Pencil size={15} /></button></div>
          <p className="mt-2 text-sm text-slate-600">{s.summary}</p>
          <p className="mt-3 text-xs text-slate-500">{s.items.length} items · {s.enabled ? "Visible" : "Hidden"}</p>
        </Card>
      ))}
      <ServiceDialog svc={editing} onClose={() => setEditing(null)} onSaved={() => { load(); reload(); }} />
    </div>
  );
}

const SETTING_FIELDS = [
  ["hero_headline", "Homepage hero headline"], ["hero_subheadline", "Homepage hero subheadline", true], ["announcement", "Announcement bar (leave empty to hide)"],
  ["ga_measurement_id", "Google Analytics Measurement ID (e.g. G-XXXXXXX)"], ["whatsapp_number", "WhatsApp number (with country code)"], ["contact_email", "Contact email"],
  ["contact_phone", "Contact phone"], ["address", "Address"], ["linkedin_url", "LinkedIn URL"], ["instagram_url", "Instagram URL"], ["twitter_url", "X / Twitter URL"], ["youtube_url", "YouTube URL"],
];

export function SettingsAdmin() {
  const { reload } = useSettings();
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setF(r.data)); }, []);
  if (!f) return null;
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", f);
      await reload();
      toast.success("Website content updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Card className="max-w-3xl">
      <form onSubmit={save} className="grid gap-5 sm:grid-cols-2" data-testid="settings-form">
        {SETTING_FIELDS.map(([k, label, long]) => (
          <div key={k} className={long || k === "hero_headline" || k === "announcement" ? "sm:col-span-2" : ""}>
            <Field label={label} id={`st-${k}`}>
              {long ? <Textarea id={`st-${k}`} rows={3} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`settings-${k}-input`} />
                : <input id={`st-${k}`} className={inputCls} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`settings-${k}-input`} />}
            </Field>
          </div>
        ))}
        <button data-testid="settings-save-btn" className="w-fit rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white">Save settings</button>
      </form>
    </Card>
  );
}
